import { useState, useEffect } from 'react';
import { Modal, useToast } from './ui/ToastModal';
import { Save, Loader2, Settings } from 'lucide-react';
import type { PnlSettings } from '../services/pnlCalculationService';

interface PnlSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: PnlSettings;
  onSave: (updates: Partial<PnlSettings>) => Promise<void>;
}

export function PnlSettingsModal({ isOpen, onClose, settings, onSave }: PnlSettingsModalProps) {
  const { addToast } = useToast();
  const [materialPercent, setMaterialPercent] = useState(settings.materialPercent);
  const [wagesMultiplierPct, setWagesMultiplierPct] = useState(settings.wagesMultiplier * 100);
  const [estimateProfit, setEstimateProfit] = useState(settings.estimateProfit);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMaterialPercent(settings.materialPercent);
      setWagesMultiplierPct(settings.wagesMultiplier * 100);
      setEstimateProfit(settings.estimateProfit);
    }
  }, [isOpen, settings]);

  const handleSave = async () => {
    if (materialPercent < 0 || materialPercent > 100) {
      addToast({ type: 'error', title: 'Validation', message: 'Materials % must be 0-100' });
      return;
    }
    if (wagesMultiplierPct < 0 || wagesMultiplierPct > 500) {
      addToast({ type: 'error', title: 'Validation', message: 'Wages multiplier must be 0-500%' });
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        materialPercent,
        wagesMultiplier: wagesMultiplierPct / 100,
        estimateProfit,
      });
      onClose();
    } catch {
      // Toast handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="P&L Financial Settings" hideFooter>
      <div style={{ padding: 'var(--spacing-xl)', maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
          <Settings size={16} />
          <span>Configure per-project financial parameters for P&L calculations.</span>
        </div>

        {/* Materials Budget % */}
        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Materials Budget Percentage</span>
            <span style={{ fontSize: 'var(--font-size-xs)', color: '#8b5cf6', fontWeight: 700, background: 'rgba(139,92,246,0.1)', padding: '2px 8px', borderRadius: 6 }}>
              {materialPercent}%
            </span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
            <input
              type="range"
              min={0}
              max={60}
              step={1}
              value={materialPercent}
              onChange={e => setMaterialPercent(Number(e.target.value))}
              style={{ flex: 1, accentColor: '#8b5cf6' }}
            />
            <div style={{ position: 'relative', width: 80 }}>
              <input data-seq-private
                type="number"
                className="form-input"
                value={materialPercent}
                onChange={e => setMaterialPercent(Number(e.target.value))}
                min={0}
                max={100}
                step={1}
                style={{ paddingRight: 24, textAlign: 'right' }}
              />
              <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>%</span>
            </div>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Automatically deducted from Construction Income before profit calculation.
          </p>
        </div>

        {/* Wages Multiplier */}
        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Wages Multiplier</span>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)', fontWeight: 700, background: 'rgba(245,158,11,0.1)', padding: '2px 8px', borderRadius: 6 }}>
              ×{(wagesMultiplierPct / 100).toFixed(2)}
            </span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
            <input
              type="range"
              min={100}
              max={200}
              step={1}
              value={wagesMultiplierPct}
              onChange={e => setWagesMultiplierPct(Number(e.target.value))}
              style={{ flex: 1, accentColor: '#f59e0b' }}
            />
            <div style={{ position: 'relative', width: 80 }}>
              <input data-seq-private
                type="number"
                className="form-input"
                value={wagesMultiplierPct}
                onChange={e => setWagesMultiplierPct(Number(e.target.value))}
                min={100}
                max={500}
                step={1}
                style={{ paddingRight: 24, textAlign: 'right' }}
              />
              <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>%</span>
            </div>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Applied to the total Wages subtotal (e.g. 104% = workers comp loading).
          </p>
        </div>

        {/* Estimated Profit */}
        <div className="form-group">
          <label className="form-label">Estimated Base Profit Target</label>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontWeight: 600 }}>$</div>
            <input data-seq-private
              type="number"
              className="form-input"
              value={estimateProfit}
              onChange={e => setEstimateProfit(Number(e.target.value))}
              step={1000}
              style={{ paddingLeft: 24 }}
              placeholder="0.00"
            />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Used to compare actual net profit against your estimate.
          </p>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-md)', borderTop: '1px solid var(--border-color)', paddingTop: 'var(--spacing-lg)' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {isSaving ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
